import {
  ApartmentOutlined,
  PartitionOutlined,
  BellOutlined,
  QuestionCircleOutlined,
  BarChartOutlined,
  AuditOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  BankOutlined,
  AppstoreOutlined,
  ProfileOutlined,
  FileTextOutlined,
  FileProtectOutlined,
  LogoutOutlined,
  SoundOutlined,
  SafetyCertificateOutlined,
  AimOutlined,
  RiseOutlined,
  TrophyOutlined,
  SettingOutlined,
  TeamOutlined,
  UsergroupAddOutlined,
  SolutionOutlined,
  WalletOutlined,
  UploadOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import { useHasRole } from '@/lib/useAuth';
import { ShellChrome } from './ShellChrome';
import { navItem } from './navItem';
import { useCollaborationNav } from './useCollaborationNav';

/**
 * The administrative shell.
 *
 * Items for modules not yet built are disabled rather than hidden, so the
 * navigation shows the shape of the product without offering routes that do not
 * exist.
 */
export function AdminShell() {
  const location = useLocation();
  const isPayrollAdmin = useHasRole('payroll_admin', 'super_admin');
  const collaboration = useCollaborationNav('/admin');

  return (
    <ShellChrome
      shell="admin"
      // Keeps the Attendance submenu open when one of its pages is showing.
      defaultOpenKeys={location.pathname.startsWith('/admin/attendance') ? ['attendance'] : []}
      // The launcher is a full page of navigation; a sidebar beside it would be
      // the same list twice.
      selfNavigatingPaths={['/admin']}
      navItems={[
        navItem('/admin', 'Home', <AppstoreOutlined />),
        ...collaboration,
        navItem('/admin/employees', 'Employees', <TeamOutlined />),
        navItem('/admin/organisation', 'Organisation', <ApartmentOutlined />),
        navItem('/admin/org-chart', 'Org chart', <PartitionOutlined />),
        {
          key: 'attendance',
          icon: <ClockCircleOutlined />,
          label: 'Attendance',
          children: [
            {
              key: '/admin/attendance',
              label: <Link to="/admin/attendance">Daily &amp; corrections</Link>,
            },
            {
              // Separate because it is a different job with a different
              // audience: HR hands a month to payroll, and it should not sit
              // one careless click away from checking in.
              key: '/admin/attendance/close',
              label: <Link to="/admin/attendance/close">Monthly close</Link>,
            },
            {
              // Where mobile attendance could not be confirmed, which handsets
              // are trusted, and what a late arrival costs (P6.18).
              key: '/admin/attendance/integrity',
              label: <Link to="/admin/attendance/integrity">Integrity &amp; policy</Link>,
            },
          ],
        },
        navItem('/admin/leave', 'Leave', <CalendarOutlined />),
        navItem('/admin/onboarding', 'Onboarding', <UsergroupAddOutlined />),
        navItem('/admin/letters', 'Letters', <FileTextOutlined />),
        navItem('/admin/exits', 'Exits', <LogoutOutlined />),
        navItem('/admin/announcements', 'Announcements', <SoundOutlined />),
        // Only shown to Payroll Admin. Hiding it is a usability choice — the
        // server refuses the routes regardless (FR-PAY-09) — but a menu entry
        // that always answers 403 teaches people to ignore menu entries.
        ...(isPayrollAdmin
          ? [
              navItem('/admin/payroll', 'Payroll runs', <BankOutlined />),
              navItem('/admin/salary', 'Salary structures', <ProfileOutlined />),
              navItem('/admin/loans', 'Loans & advances', <WalletOutlined />),
              // Payroll pays approved claims; it does not approve them, which
              // is why a manager sees this queue too and from a different shell.
              navItem('/admin/claims', 'Expense claims', <SolutionOutlined />),
              navItem('/admin/bulk', 'Bulk actions', <UploadOutlined />),
              // Proof season. Payroll's alone, like the rest of this group.
              navItem('/admin/tax-declarations', 'Tax declarations', <FileProtectOutlined />),
            ]
          : []),
        navItem("/admin/performance", "Performance", <TrophyOutlined />),
        navItem("/admin/goals", "Goals & OKRs", <AimOutlined />),
        navItem("/admin/improvement-plans", "Improvement plans", <RiseOutlined />),
        navItem("/admin/reports", "Reports", <BarChartOutlined />),
        navItem('/admin/privacy', 'Privacy & Consent', <SafetyCertificateOutlined />),
        navItem('/admin/audit', 'Audit trail', <AuditOutlined />),
        navItem('/admin/notifications', 'Notifications', <BellOutlined />),
        navItem('/admin/help', 'Get help', <QuestionCircleOutlined />),
        // Bank account changes sit outside Configuration because they are a
        // queue somebody works, not a setting somebody sets.
        navItem('/admin/bank-changes', 'Bank changes', <IdcardOutlined />),
        {
          key: 'configuration',
          icon: <SettingOutlined />,
          label: 'Configuration',
          children: [
            {
              key: '/admin/leave-config',
              label: <Link to="/admin/leave-config">Leave &amp; holidays</Link>,
            },
            {
              key: '/admin/access',
              label: <Link to="/admin/access">Roles &amp; access</Link>,
            },
          ],
        },
      ]}
    />
  );
}
